import { Box } from '@mui/material';
import { format, isToday } from 'date-fns';

import { MonthEventPill } from '../components/MonthEventPill';
import type { CalendarEvent, CalendarView, ISODate, Member } from '../types';
import { isoMonth, monthGridDates, parseISODate } from '../utils';

const MAX_VISIBLE = 3;

// Day-of-week header labels indexed by weekday (0=Sun…6=Sat)
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type MonthViewProps = {
  cursor: ISODate;
  events: CalendarEvent[];
  members?: Member[];
  selectedEventId?: string | null;
  selectedMemberIds?: string[] | null;
  weekStartsOn?: 0 | 1;
  onEventClick: (event: CalendarEvent) => void;
  onDayClick: (date: ISODate) => void;
  onViewChange: (view: CalendarView) => void;
  onCursorChange: (cursor: ISODate) => void;
};

function sortEvents(events: CalendarEvent[]): CalendarEvent[] {
  const order = { session: 0, block: 1, override: 2 };
  return [...events].sort((a, b) => order[a.kind] - order[b.kind] || a.start - b.start);
}

export function MonthView({
  cursor,
  events,
  members = [],
  selectedEventId,
  selectedMemberIds,
  weekStartsOn = 1,
  onEventClick,
  onDayClick,
  onViewChange,
  onCursorChange,
}: MonthViewProps) {
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const currentMonth = cursor.slice(0, 7);
  const gridDates = monthGridDates(cursor, weekStartsOn);

  // Build header labels in weekStartsOn order
  const headerDays = Array.from({ length: 7 }, (_, i) => DAY_NAMES[(weekStartsOn + i) % 7]);

  function isVisible(ev: CalendarEvent): boolean {
    if (ev.kind !== 'session') return true;
    if (!selectedMemberIds) return true;
    if (!ev.memberId) return true;
    return selectedMemberIds.includes(ev.memberId);
  }

  function handlePillClick(event: CalendarEvent) {
    onEventClick(event);
    // Consumer decides whether to switch views — we don't impose that here.
  }

  function handleDayClick(date: ISODate) {
    onDayClick(date);
    onCursorChange(date);
    onViewChange('week');
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'auto',
        background: '#fff',
      }}
    >
      {/* Day-of-week headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          background: '#fafaf8',
          borderBottom: '1px solid #e8e5e0',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        {headerDays.map((name) => (
          <Box
            key={name}
            sx={{
              padding: '8px 10px',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 500,
              color: '#888',
              borderRight: '1px solid #f0ede8',
              textAlign: 'center',
              '&:last-child': { borderRight: 'none' },
            }}
          >
            {name}
          </Box>
        ))}
      </Box>

      {/* 6×7 day grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: 'minmax(90px, 1fr)',
          flex: 1,
        }}
      >
        {gridDates.map((date) => {
          const d = parseISODate(date);
          const isCurrentMonth = isoMonth(date) === currentMonth;
          const todayFlag = isToday(d);

          const dayEvents = sortEvents(events.filter((ev) => ev.date === date && isVisible(ev)));
          const visible = dayEvents.slice(0, MAX_VISIBLE);
          const overflow = dayEvents.length - MAX_VISIBLE;

          return (
            <Box
              key={date}
              onClick={() => handleDayClick(date)}
              sx={{
                borderRight: '1px solid #f0ede8',
                borderBottom: '1px solid #f0ede8',
                padding: '6px 8px',
                overflow: 'hidden',
                cursor: 'pointer',
                background: isCurrentMonth ? '#fff' : '#fafaf8',
                '&:hover': { background: isCurrentMonth ? '#fafaf8' : '#f5f5f3' },
                '&:nth-of-type(7n)': { borderRight: 'none' },
              }}
            >
              {/* Date number */}
              <Box sx={{ mb: '4px' }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: isCurrentMonth ? '#222' : '#ccc',
                    ...(todayFlag && {
                      background: '#2a3a22',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 22,
                      height: 22,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 12,
                    }),
                  }}
                >
                  {format(d, 'd')}
                </Box>
              </Box>

              {/* Event pills */}
              {visible.map((ev) => (
                <MonthEventPill
                  key={ev.id}
                  event={ev}
                  member={ev.memberId ? memberMap.get(ev.memberId) : undefined}
                  isSelected={ev.id === selectedEventId}
                  onClick={handlePillClick}
                />
              ))}

              {overflow > 0 && (
                <Box
                  sx={{
                    fontSize: 10,
                    color: '#666',
                    padding: '2px 6px',
                    cursor: 'pointer',
                    '&:hover': { color: '#222' },
                  }}
                >
                  +{overflow} more
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
